// liveFeedGate — realtime-consistency guards shared by the Discover / Pulse /
// search live feeds (REALTIME-CONSISTENCY).
//
// The bug class this kills: a WS trade patches a visible row forward
// (volume/mcap/txns/sparkline), then a REST refetch or a `pulse.aggregate`
// event computed from a QuestDB read that does NOT yet contain that trade
// (ILP/WAL visibility lag) lands and silently rewinds the row. The next trade
// jumps it forward again — a visible sawtooth on every actively-trading token.
//
// Rules encoded here:
//   • dedup — the same trade (structural identity) is applied at most once;
//   • ordering — an older trade never overwrites newer price-like fields
//     (per-token ts, then lt as the same-second tiebreak);
//   • monotonic snapshots — a snapshot row that carries FEWER txns than the
//     live row while that token traded within the staleness guard window is
//     provably behind the live tail: keep the live trade-derived values and
//     adopt only identity/metadata from it. A quiet token (no live trade for
//     STALE_GUARD_MS) accepts the snapshot wholesale so 24h-window decay and
//     server corrections still flow.
//
// txns count is the ordering key between a live row and a snapshot: both count
// the same per-token trade stream, so snapshot.txns >= live.txns ⇔ the
// snapshot includes every trade the client has applied.

import type { PulseItem, TokenSummary, TradeSummary, WindowTradeEvent } from '../api/types'
import { rowKeyOf, tradeRowKey } from './discoverRowKey'
import { canonicalTokenBirthMs } from './tokenAge'

// Covers observed local ILP→WAL→query visibility lag (single seconds) with
// headroom; short enough that window decay on a quiet token is delayed at most
// one refresh cycle.
export const STALE_GUARD_MS = 10_000

const MAX_SEEN_TRADES = 4096
const MAX_TRACKED_TOKENS = 2048

interface TokenLiveMark {
  appliedAtMs: number
  ts: number
  lt: number
}

interface TokenPriceCursor {
  ts: number
  lt?: number
}

export interface TradeAdmission {
  /** false = duplicate delivery; skip entirely (unless it is a `patch`). */
  fresh: boolean
  /** false = older than an already-applied trade for this token; counters may
      still accumulate but price-like fields must not be overwritten. */
  newest: boolean
  /**
   * The valued upgrade of an already-applied pending frame (stage-3 B1: the
   * fast lane publishes the frame at MC-applied speed with mc_status:"pending"
   * and the valuation follows as a second frame with the same identity).
   * Counters were already applied — the consumer must apply ONLY what the
   * pending frame could not carry: the USD volume share, price_usd/mcap and
   * the sparkline point.
   */
  patch?: boolean
}

const MAX_PENDING_VALUATION = 1024

export class LiveTradeGate {
  private seen = new Set<string>()
  private byToken = new Map<string, TokenLiveMark>()
  private coveredTrades = new Set<string>()
  /** Coverage keys whose observed window event already carried quote_usd —
      proof the snapshot's aggregates include that trade's USD share. */
  private valuedCovered = new Set<string>()
  private coveredByToken = new Map<string, TokenPriceCursor>()
  /** Keys admitted while still unvalued — the patch with the same key upgrades. */
  private pendingValuation = new Set<string>()

  admit(trade: TradeSummary, nowMs: number = Date.now()): TradeAdmission {
    const key = tradeKey(trade)
    // The valued patch of a frame admitted while pending must win over every
    // duplicate classification: applying the pending frame merged its (still
    // unvalued) window event into the row, and the state effect re-observes
    // those rows into coverage — so by the time the patch arrives, its own
    // pending frame is usually "covered". tradeKey is identical across both
    // frames by construction (it carries no valuation fields).
    if (key && this.pendingValuation.has(key) && tradeCarriesValuation(trade)) {
      this.pendingValuation.delete(key)
      const coveredKey = tradeCoverageKey(trade)
      if (coveredKey && this.valuedCovered.has(coveredKey)) {
        // A snapshot already healed the row (its event carries quote_usd):
        // applying the patch now would add the USD share a second time.
        return { fresh: false, newest: false }
      }
      return { fresh: false, newest: this.isNewestFor(trade), patch: true }
    }
    const coveredKey = tradeCoverageKey(trade)
    if (coveredKey && this.coveredTrades.has(coveredKey)) {
      return { fresh: false, newest: false }
    }
    if (key && this.seen.has(key)) {
      return { fresh: false, newest: false }
    }
    if (key) {
      this.rememberKey(key)
      if (trade.mc_status === 'pending') this.rememberPendingValuation(key)
    }
    const token = tradeTokenKey(trade)
    if (!token) return { fresh: true, newest: true }
    const candidate = tradeCursor(trade)
    const covered = this.coveredByToken.get(token)
    const newerThanSnapshot =
      candidate == null || covered == null || compareCursor(candidate, covered) > 0
    return { fresh: true, newest: this.markToken(token, trade, nowMs) && newerThanSnapshot }
  }

  /** Public form of isNewestFor — for patches applied OUTSIDE admit() (the
      claimUnvaluedCoverage path), so price-like fields still obey ordering. */
  isNewestKnownFor(trade: TradeSummary): boolean {
    return this.isNewestFor(trade)
  }

  /** Is this trade still the newest applied for its token? Decides whether a
      valued patch may move price-like fields or only accumulate USD volume. */
  private isNewestFor(trade: TradeSummary): boolean {
    const token = tradeTokenKey(trade)
    if (!token) return true
    const mark = this.byToken.get(token)
    if (!mark) return true
    const ts = Number.isFinite(trade.ts) ? trade.ts : 0
    const lt = typeof trade.lt === 'number' && Number.isFinite(trade.lt) ? trade.lt : 0
    return ts > mark.ts || (ts === mark.ts && lt >= mark.lt)
  }

  private rememberPendingValuation(key: string): void {
    this.pendingValuation.add(key)
    if (this.pendingValuation.size > MAX_PENDING_VALUATION) {
      const oldest = this.pendingValuation.values().next().value
      if (oldest !== undefined) this.pendingValuation.delete(oldest)
    }
  }

  /** Replace REST coverage with exact trade identities from the immutable tape.
      A price cursor is not coverage: a late older event can be absent from the
      snapshot even though a newer price edge exists. */
  observeTokenSnapshots(rows: TokenSummary[]): void {
    this.coveredTrades.clear()
    this.valuedCovered.clear()
    for (const row of rows) {
      for (const event of row.window_events ?? []) {
        const key = windowEventCoverageKey(event)
        if (!key) continue
        this.coveredTrades.add(key)
        if (typeof event.quote_usd === 'number' && Number.isFinite(event.quote_usd)) {
          this.valuedCovered.add(key)
        }
      }
      const token = rowKeyOf(row)
      const cursor = priceWindowCursor(row)
      if (!token || !cursor) continue
      const previous = this.coveredByToken.get(token)
      if (!previous || compareCursor(cursor, previous) > 0) {
        this.coveredByToken.set(token, cursor)
      }
    }
  }

  private rememberKey(key: string): void {
    this.seen.add(key)
    if (this.seen.size > MAX_SEEN_TRADES) {
      const oldest = this.seen.values().next().value
      if (oldest !== undefined) this.seen.delete(oldest)
    }
  }

  private markToken(token: string, trade: TradeSummary, nowMs: number): boolean {
    const prev = this.byToken.get(token)
    const ts = Number.isFinite(trade.ts) ? trade.ts : 0
    const lt = typeof trade.lt === 'number' && Number.isFinite(trade.lt) ? trade.lt : 0
    const newest = !prev || ts > prev.ts || (ts === prev.ts && lt >= prev.lt)
    this.byToken.set(token, {
      appliedAtMs: nowMs,
      ts: newest ? ts : prev.ts,
      lt: newest ? lt : prev.lt,
    })
    if (this.byToken.size > MAX_TRACKED_TOKENS) {
      const oldest = this.byToken.keys().next().value
      if (oldest !== undefined) this.byToken.delete(oldest)
    }
    return newest
  }

  /** A live trade was applied for this token within the staleness guard —
      a snapshot regressing its counters is behind the live tail, not decay. */
  hasRecentLive(token: string, nowMs: number = Date.now()): boolean {
    const mark = this.byToken.get(token)
    return mark != null && nowMs - mark.appliedAtMs <= STALE_GUARD_MS
  }

  /** A frame carrying valuation whose trade the last snapshot covered UNVALUED:
      the snapshot's aggregates provably miss its USD share (the tape event had
      no quote_usd), so the consumer may apply a USD-only patch. One-shot — the
      claim marks the key valued so a replay cannot re-apply. */
  claimUnvaluedCoverage(trade: TradeSummary): boolean {
    if (!tradeCarriesValuation(trade)) return false
    const coveredKey = tradeCoverageKey(trade)
    if (!coveredKey || !this.coveredTrades.has(coveredKey)) return false
    if (this.valuedCovered.has(coveredKey)) return false
    this.valuedCovered.add(coveredKey)
    return true
  }

  reset(): void {
    this.seen.clear()
    // With seen gone, an in-flight trade's valued frame will re-admit as fresh
    // and full-apply — a surviving pending slot would then misclassify the
    // socket's replay of that same frame as a patch and add its USD twice.
    this.pendingValuation.clear()
    // Keep ordering + exact REST coverage across reconnects. The socket may replay
    // old messages before the catch-up fetch lands; forgetting either boundary
    // would count them twice and briefly rewind price/window state.
  }
}

/** A frame that carries what the pending frame could not. */
export function tradeCarriesValuation(trade: TradeSummary): boolean {
  if (trade.mc_status === 'pending') return false
  return (
    (typeof trade.price_usd === 'number' && Number.isFinite(trade.price_usd)) ||
    (typeof trade.mcap_usd === 'number' && Number.isFinite(trade.mcap_usd)) ||
    trade.mc_status === 'unavailable'
  )
}

function tradeCursor(trade: TradeSummary): TokenPriceCursor | null {
  if (!Number.isFinite(trade.ts)) return null
  return {
    ts: trade.ts,
    ...(typeof trade.lt === 'number' && Number.isFinite(trade.lt) ? { lt: trade.lt } : {}),
  }
}

function compareCursor(left: TokenPriceCursor, right: TokenPriceCursor): number {
  if (left.ts !== right.ts) return left.ts < right.ts ? -1 : 1
  const leftHasLt = typeof left.lt === 'number' && Number.isFinite(left.lt)
  const rightHasLt = typeof right.lt === 'number' && Number.isFinite(right.lt)
  if (leftHasLt && rightHasLt) {
    if (left.lt === right.lt) return 0
    return (left.lt as number) < (right.lt as number) ? -1 : 1
  }
  if (leftHasLt) return 1
  if (rightHasLt) return -1
  return 0
}

function tradeCoverageKey(trade: TradeSummary): string {
  return exactCoverageKey(
    trade.pool_addr,
    trade.ts,
    trade.lt,
    trade.side,
    trade.quote_amount,
    trade.tx_hash
  )
}

function windowEventCoverageKey(event: WindowTradeEvent): string {
  return exactCoverageKey(
    event.pool_addr,
    event.ts,
    event.lt,
    event.side,
    event.quote_native,
    event.tx_hash
  )
}

function exactCoverageKey(
  pool: string | undefined,
  ts: number,
  lt: number | undefined,
  side: string,
  quote: number,
  txHash: string
): string {
  if (!pool || !Number.isFinite(ts)) return ''
  if (typeof lt === 'number' && Number.isFinite(lt) && Number.isFinite(quote)) {
    return JSON.stringify([pool, ts, lt, side, quote])
  }
  if (!txHash) return ''
  return JSON.stringify([pool, ts, txHash])
}

// Structural trade identity (same vocabulary as the native feed's dedup): the
// tx_hash alone is not unique for multi-swap transactions.
export function tradeKey(t: TradeSummary): string {
  if (!t.tx_hash) return ''
  return [
    t.tx_hash,
    t.pool_addr ?? '',
    t.base_token ?? '',
    t.side,
    t.ts,
    t.base_amount,
    t.quote_amount,
  ].join(':')
}

// A live mark per Discover row: the token's main row for TON-quoted trades,
// a pool's own row for a pool without TON.
function tradeTokenKey(t: TradeSummary): string {
  return tradeRowKey(t)
}

// --- snapshot reconciliation -------------------------------------------------

// Fields a stale snapshot must NOT rewind on a token that traded within the
// guard window: everything the live trade tail derives. Identity/metadata
// (ticker, name, image, socials, dex, liquidity source fields, age…) always
// come from the snapshot — it is authoritative for everything except the
// live trade tail.
function keepLiveTokenValues(snap: TokenSummary, live: TokenSummary): TokenSummary {
  return {
    ...snap,
    last_price: live.last_price,
    price_usd: live.price_usd,
    mcap_usd: live.mcap_usd,
    liquidity_usd: live.liquidity_usd ?? snap.liquidity_usd,
    volume_24h_native: Math.max(live.volume_24h_native, snap.volume_24h_native),
    vol_recent_native: live.vol_recent_native ?? snap.vol_recent_native,
    // The Discover rank reads these two (USD at trade time): a stale snapshot
    // must not rewind them either.
    volume_window_usd: maxKnown(live.volume_window_usd, snap.volume_window_usd),
    vol_recent_usd: live.vol_recent_usd ?? snap.vol_recent_usd,
    txns_24h: live.txns_24h,
    txns_24h_buys: live.txns_24h_buys,
    txns_24h_sells: live.txns_24h_sells,
    bonding_progress_pct: live.bonding_progress_pct ?? snap.bonding_progress_pct,
    price_change_24h_pct: live.price_change_24h_pct,
    price_window: live.price_window ?? snap.price_window,
    window_events: live.window_events ?? snap.window_events,
    // The live series is the previous snapshot base plus the applied trade
    // tail — the stale snapshot's series is missing exactly that tail (the
    // visible "sparkline collapses then regrows" symptom).
    sparkline: live.sparkline ?? snap.sparkline,
  }
}

function snapshotRegresses(snapTxns: number | undefined, liveTxns: number | undefined): boolean {
  return (snapTxns ?? 0) < (liveTxns ?? 0)
}

// reconcileTokenSnapshot merges a REST /v1/tokens snapshot over the live rows.
// Membership and base order stay the snapshot's (it is the ranked list); each
// row that provably lags the live trade tail keeps its live values.
export function reconcileTokenSnapshot(
  live: TokenSummary[],
  snapshot: TokenSummary[],
  gate: LiveTradeGate,
  nowMs: number = Date.now()
): TokenSummary[] {
  if (live.length === 0) return snapshot
  const byRow = new Map(live.map((r) => [rowKeyOf(r), r]))
  return snapshot.map((snap) => {
    const cur = byRow.get(rowKeyOf(snap))
    if (!cur) return snap
    const hasRecentLive = gate.hasRecentLive(rowKeyOf(snap), nowMs)
    const snapshotAdvancesLiveTail = (snap.txns_24h ?? 0) > (cur.txns_24h ?? 0)
    const sameProjectionRoute = snap.primary_pool_addr === cur.primary_pool_addr
    if (
      sameProjectionRoute &&
      (cur.price_window?.status === 'stale' || (hasRecentLive && !snapshotAdvancesLiveTail)) &&
      !snapshotCoversWindow(snap, cur)
    ) {
      return keepLiveTokenValues(snap, cur)
    }
    if (!hasRecentLive) return snap
    if (!snapshotRegresses(snap.txns_24h, cur.txns_24h)) return snap
    return keepLiveTokenValues(snap, cur)
  })
}

function snapshotCoversWindow(snapshot: TokenSummary, live: TokenSummary): boolean {
  const snapshotCursor = priceWindowCursor(snapshot)
  const liveCursor = priceWindowCursor(live)
  return (
    snapshotCursor != null && liveCursor != null && compareCursor(snapshotCursor, liveCursor) >= 0
  )
}

function priceWindowCursor(row: TokenSummary): TokenPriceCursor | null {
  const source = row.price_window
  if (!source?.current_source_ts) return null
  const parsedMs = Date.parse(source.current_source_ts)
  if (!Number.isFinite(parsedMs)) return null
  return {
    ts: Math.floor(parsedMs / 1000),
    ...(typeof source.current_source_lt === 'number' && Number.isFinite(source.current_source_lt)
      ? { lt: source.current_source_lt }
      : {}),
  }
}

function keepLivePulseValues(snap: PulseItem, live: PulseItem): PulseItem {
  return {
    ...snap,
    mcap_usd: live.mcap_usd,
    fdv_usd: live.fdv_usd ?? snap.fdv_usd,
    volume_24h_native: Math.max(live.volume_24h_native ?? 0, snap.volume_24h_native ?? 0),
    volume_24h_usd: live.volume_24h_usd ?? snap.volume_24h_usd,
    txns_24h: live.txns_24h,
    txns_24h_buys: live.txns_24h_buys,
    txns_24h_sells: live.txns_24h_sells,
    bonding_progress_pct: live.bonding_progress_pct ?? snap.bonding_progress_pct,
    progress_known: live.progress_known || snap.progress_known,
    window_events_ready: live.window_events_ready || snap.window_events_ready,
    window_events: live.window_events ?? snap.window_events,
  }
}

// reconcilePulseAggregateItem merges one `pulse.aggregate` WS payload (a
// REST-shaped card snapshot the indexer reads back from QuestDB right after
// writing a trade — often before that trade is visible) over the live card.
export function reconcilePulseAggregateItem(
  live: PulseItem,
  agg: PulseItem,
  gate: LiveTradeGate,
  nowMs: number = Date.now()
): PulseItem {
  if (!gate.hasRecentLive(live.jetton_master, nowMs)) return agg
  if (!snapshotRegresses(agg.txns_24h, live.txns_24h)) return agg
  return keepLivePulseValues(agg, live)
}

// reconcilePulseSnapshot merges a REST /v1/pulse reload over live items. The
// response wins membership, EXCEPT cards the live tail knows about that the
// (lagging) snapshot misses: a deploy upserted via WS seconds ago must not
// vanish on a reconnect reseed. Render-time partitioning re-sorts by
// deploy/migrate timestamps, so append order here is irrelevant.
export function reconcilePulseSnapshot(
  live: PulseItem[],
  snapshot: PulseItem[],
  gate: LiveTradeGate,
  nowMs: number = Date.now()
): PulseItem[] {
  if (live.length === 0) return snapshot
  const liveByMaster = new Map(live.map((r) => [r.jetton_master, r]))
  const merged = snapshot.map((snap) => {
    const cur = liveByMaster.get(snap.jetton_master)
    if (!cur) return snap
    if (!gate.hasRecentLive(snap.jetton_master, nowMs)) return snap
    if (!snapshotRegresses(snap.txns_24h, cur.txns_24h)) return snap
    return keepLivePulseValues(snap, cur)
  })
  const inSnapshot = new Set(snapshot.map((r) => r.jetton_master))
  for (const cur of live) {
    if (inSnapshot.has(cur.jetton_master)) continue
    // Keep only cards the snapshot plausibly missed due to lag: freshly
    // deployed or freshly traded. Everything else the snapshot dropped on
    // purpose (dead/evicted).
    if (gate.hasRecentLive(cur.jetton_master, nowMs) || isRecentDeploy(cur, nowMs)) {
      merged.push(cur)
    }
  }
  return merged
}

const RECENT_DEPLOY_MS = 10 * 60_000

function isRecentDeploy(item: PulseItem, nowMs: number): boolean {
  const birthMs = canonicalTokenBirthMs(item.birth_ts ?? item.deploy_ts, nowMs)
  return birthMs != null && nowMs - birthMs <= RECENT_DEPLOY_MS
}

// patchTokenMetadata applies a `token.metadata` event in place (identity only;
// never a full-list refetch — REALTIME-CONSISTENCY). Returns the same array
// when the token is not on the board.
export function patchTokenMetadata(
  rows: TokenSummary[],
  d: { jetton_master: string; ticker?: string; name?: string; image_url?: string }
): TokenSummary[] {
  if (!d.jetton_master) return rows
  // Identity is the token's: every row of it (its main row and its pool rows).
  if (!rows.some((r) => r.jetton_master === d.jetton_master)) return rows
  return rows.map((cur) =>
    cur.jetton_master === d.jetton_master
      ? {
          ...cur,
          ticker: d.ticker || cur.ticker,
          name: d.name || cur.name,
          image_url: d.image_url || cur.image_url,
        }
      : cur
  )
}

// The larger of two optional volumes; null only when neither is known.
function maxKnown(a: number | null | undefined, b: number | null | undefined): number | null {
  const known = [a, b].filter((v): v is number => typeof v === 'number' && Number.isFinite(v))
  return known.length === 0 ? null : Math.max(...known)
}
